import React, {FC, ReactNode, useState} from 'react';

import {STEP_TO_ACTION} from '../../constants';
import {usePersistActorSnapshot} from '../../machine/hooks/usePersistActorSnapshot';
import {useTrackStepEntry} from '../../machine/hooks/useTrackStepEntry';
import type {FhsStepName} from '../../types';
import {logFhsStepEntered} from '../../utils';
import {
  readCurrentAnswersSnapshot,
  writeCurrentAnswersSnapshot,
} from '../storage';

import {FhsContext} from './actorContext';

interface FhsProviderProps {
  children: ReactNode;
}

const SnapshotPersister: FC = () => {
  const actor = FhsContext.useActorRef();

  usePersistActorSnapshot(actor, writeCurrentAnswersSnapshot);

  return null;
};

/* Hoisted so the reference is stable. Inline, it was rebuilt every render and
   reached a dependency inside useTrackStepEntry, re-subscribing each time. It
   closes over nothing from the component. */
const logStepEntry = (stepName: string): void => {
  const action = STEP_TO_ACTION[stepName as FhsStepName];

  if (action) logFhsStepEntered(action);
};

const StepTracker: FC = () => {
  const actor = FhsContext.useActorRef();

  useTrackStepEntry(actor, logStepEntry);

  return null;
};

// Wraps FhsContext.Provider so the actor hydrates from localStorage on mount
// and persists every transition back. useState's lazy initializer runs once per
// mount; the Suspense boundary in the module entry bails out of SSR via
// useSearchParams, so this subtree only renders on the client.
export const FhsProvider: FC<FhsProviderProps> = ({children}) => {
  const [snapshot] = useState(() => readCurrentAnswersSnapshot());

  return (
    <FhsContext.Provider options={{snapshot}}>
      <SnapshotPersister />
      <StepTracker />
      {children}
    </FhsContext.Provider>
  );
};
